






































































































































import { Component } from "vue-property-decorator";
import MiraBarRowComponent from "@/components/mira/MiraBarRow.vue";
import MiraDetailCardComponent from "@/components/mira/MiraDetailCard.vue";
import MiraDonutWithLegendComponent from "@/components/mira/MiraDonutWithLegend.vue";
import MiraKpiTileComponent from "@/components/mira/MiraKpiTile.vue";
import MiraKpiTileGridComponent from "@/components/mira/MiraKpiTileGrid.vue";
import MiraComparisonCardComponent from "@/components/mira/MiraComparisonCard.vue";
import MiraScreenBase from "@/components/mira/MiraScreenBase";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraTrendCardComponent from "@/components/mira/MiraTrendCard.vue";
import MiraStackedTrendCardComponent from "@/components/mira/MiraStackedTrendCard.vue";
import MiraUnlockListComponent from "@/components/mira/MiraUnlockList.vue";
import { MiraSection } from "@/data/models/Mira";
import { COLOR_MIRA_BAR_TRACK, COLOR_MIRA_BAR_TRACK_DARK } from "@/utils/colors";
import {
  MiraBar,
  MiraSegment,
  MiraTile,
  buildEnergySegments,
  buildFieldRows,
  buildStatusLegend,
  buildTiles,
  caveatText,
  dataQualityTrendFormatter,
  energyImplausibleProjectsNote,
  formatPercent,
  isApplicable,
  publishedMetric,
} from "@/utils/MiraDataQualityView";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";

/**
 * Mira Datenqualität: how completely the signed-in client captures what the other Mira figures
 * need, and which figures are hidden until it captures more.
 *
 * A KPI row of the four figures (value, change against the comparison period, status), then each
 * figure's breakdown, then the unlock list across every section. Loading, the period picker and the
 * header come from `MiraScreenBase`; the rules are in `@/utils/MiraDataQuality`, what each tile and bar
 * shows in `@/utils/MiraDataQualityView`. See `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-bar-row": MiraBarRowComponent,
    "mira-detail-card": MiraDetailCardComponent,
    "mira-donut-with-legend": MiraDonutWithLegendComponent,
    "mira-kpi-tile": MiraKpiTileComponent,
    "mira-kpi-tile-grid": MiraKpiTileGridComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-comparison-card": MiraComparisonCardComponent,
    "mira-trend-card": MiraTrendCardComponent,
    "mira-stacked-trend-card": MiraStackedTrendCardComponent,
    "mira-unlock-list": MiraUnlockListComponent,
  },
})
export default class MiraDataQualityComponent extends MiraScreenBase {
  get section(): MiraSection {
    return MiraSection.DATA_QUALITY;
  }

  get titleKey(): string {
    return "mira.dataQuality.title";
  }

  get tiles(): MiraTile[] {
    return buildTiles(this.period, this.comparison, this.viewContext);
  }

  get fieldsSubtitle(): string | null {
    const metric = publishedMetric(this.period, "MIRA-DQ-001");
    return metric ? this.$t("mira.dataQuality.fieldsSubtitle", [formatNumber(metric.basis ?? 0)]).toString() : null;
  }

  get fieldCoverageCaveat(): string | null {
    return this.caveatOf("MIRA-DQ-001");
  }

  get fieldRows(): MiraBar[] {
    return buildFieldRows(this.period, this.comparison, this.viewContext);
  }

  get statusLegend(): Array<{ label: string; color: string }> {
    return buildStatusLegend(this.viewContext);
  }

  get energySegments(): MiraSegment[] {
    return buildEnergySegments(this.period, this.viewContext);
  }

  /** The ring's centre: the share with readings, the same figure the tile leads with. */
  get energyShareText(): string {
    const metric = publishedMetric(this.period, "MIRA-DQ-002");
    return metric ? formatPercent(metric.value ?? 0, this.viewContext) : "";
  }

  get energyAriaLabel(): string {
    return this.energySegments.map((segment) => `${segment.label}: ${segment.valueText}`).join(", ");
  }

  get ringTrackColor(): string {
    return themed(COLOR_MIRA_BAR_TRACK, COLOR_MIRA_BAR_TRACK_DARK);
  }

  get energyImplausibleProjectsNote(): string | null {
    return energyImplausibleProjectsNote(this.period, this.viewContext);
  }

  /** Whether the figure can apply to the client in the chosen period; its card is hidden if not. */
  applies(metricId: string): boolean {
    return isApplicable(this.period, metricId);
  }

  caveatOf(metricId: string): string | null {
    return caveatText(publishedMetric(this.period, metricId), this.viewContext);
  }

  trendFormatter(metricId: string): (value: number) => string {
    return dataQualityTrendFormatter(metricId, this.viewContext);
  }
}
